import { createSelector } from "@reduxjs/toolkit";
import { openapi } from "../../../../openapi";
import { selectHttpProjectMembers } from "../../projectSelectors";
import { IRootState } from "../../../store";
import { selectFilteredIssuesOnBoard } from "./issueSelectors";

const selectCurrentProjectId = (state: IRootState) => state.globalReducer.currentProjectId;
const selectCurrentIssueBoardId = (state: IRootState) => state.issueReducerNew.currentIssueBoardId;

export const selectIssueAskAiPanelOpen = (state: IRootState) => state.issueReducerNew.issueAskAiPanelOpen;

// Defers the circular issue selector read until Redux evaluates the selector.
const selectVisibleIssuesOnBoard = (state: IRootState) => selectFilteredIssuesOnBoard(state);

export const selectIssueAskAiHttpArgs = createSelector(
  [selectCurrentProjectId, selectCurrentIssueBoardId],
  (projectId, issueboardId): string | null => {
    if (projectId == null || issueboardId == null) {
      return null;
    }

    return `issueAskAiFilter:${projectId}:${issueboardId}`;
  },
);

const selectIssueAskAiHttpResult = createSelector(
  [(state: IRootState) => state, selectIssueAskAiHttpArgs],
  (state, args) => {
    if (args == null) {
      return null;
    }

    return openapi.endpoints.postProjectByProjectIdIssueboardAndIssueboardIdIssuesOnBoardAskAi.select(args)(state);
  },
);

export const selectIssueAskAiFilter = createSelector([selectIssueAskAiHttpResult], (issueAskAiHttpResult) => {
  const data = issueAskAiHttpResult?.data;
  if (data == null || data.unsupported != null || data.explanation == null) {
    return null;
  }

  return {
    projectId: data.request.projectId,
    issueboardId: data.request.issueboardId,
    question: data.request.question,
    issues: data.issues,
    explanation: data.explanation,
    debug: data.debug,
  };
});

export const selectIssueAskAiFilterOnCurrentBoard = createSelector(
  [selectIssueAskAiFilter, selectCurrentProjectId, selectCurrentIssueBoardId],
  (issueAskAiFilter, projectId, issueBoardId) =>
    issueAskAiFilter != null &&
    issueAskAiFilter.projectId === projectId &&
    issueAskAiFilter.issueboardId === issueBoardId
      ? issueAskAiFilter
      : null,
);

export const selectIsIssueAskAiSemanticSearchActive = createSelector(
  [selectIssueAskAiPanelOpen, selectIssueAskAiFilterOnCurrentBoard],
  (isPanelOpen, issueAskAiFilter) => isPanelOpen === true && issueAskAiFilter?.explanation.mode === "SEMANTIC_SEARCH",
);

export const selectIssueAskAiResultVisibilitySummary = createSelector(
  [selectIssueAskAiPanelOpen, selectIssueAskAiFilterOnCurrentBoard, selectVisibleIssuesOnBoard],
  (isIssueAskAiPanelOpen, issueAskAiFilter, visibleIssues) => {
    if (isIssueAskAiPanelOpen !== true || issueAskAiFilter == null) {
      return null;
    }

    const totalAiResults = issueAskAiFilter.issues.length;
    const visibleAiResults = visibleIssues.length;
    return {
      totalAiResults,
      visibleAiResults,
      hiddenByFrontendFilters: Math.max(totalAiResults - visibleAiResults, 0),
    };
  },
);

export const selectIssueAskAiPeopleOnCurrentBoard = createSelector(
  [selectIssueAskAiFilterOnCurrentBoard, selectHttpProjectMembers],
  (issueAskAiFilter, projectMembers) => {
    const people = issueAskAiFilter?.explanation.people ?? [];
    const projectMembersByUserId = new Map(projectMembers.map((member) => [member.user._id, member]));
    const seenMemberIds = new Set<string>();

    return people.flatMap((person) => {
      const projectMember = projectMembersByUserId.get(person.userId);
      if (projectMember == null || seenMemberIds.has(projectMember._id)) {
        return [];
      }

      seenMemberIds.add(projectMember._id);
      return [{ ...person, projectMember }];
    });
  },
);
